import React, { useEffect, useState, useMemo } from "react";
import { Table } from "semantic-ui-react";
import { Pie, Column } from "@ant-design/plots";
import { coins_units } from "../../../../utils";
import { useWindowWidth } from "../../../../hooks/useWindowWidth";
import { EmptyChartMessage } from "../EmptyChartMessage";
import { convertEnergy2 } from "../../../../utils/dataAndFormulas/convertEnergy";
import { getDonutHeight, BAR_PALETTE, DONUT_COLORS } from "../../../../utils/chartConfig";
import { formatWhole, formatMagnitudeAdaptive, formatPercentage } from "../../../../utils/numberFormat";
import styles from "./TableGraf.scss";

// Mapeo de tus categorías (Ene1, Ene2, ...) con las claves del objeto totalsPerYear
const energyGroups = {
  Ene1: [
    // Electricidad de Red
    "electricity_standard_combined",
    "electricity_standard_domestic",
    "electricity_standard_production",
  ],
  Ene2: [
    // Renovables PPA
    "renewable_energies_ppa_combined",
    "renewable_energies_ppa_domestic",
    "renewable_energies_ppa_production",
  ],
  Ene3: [
    // Renovables On-Site
    "renewable_energies_on_site_combined",
    "renewable_energies_on_site_domestic",
    "renewable_energies_on_site_production",
  ],
  Ene4: [
    // CoGeneración Gas Natural
    "natural_gas_cogeneration",
    "natural_gas_combined",
    "natural_gas_production_operations",
    "natural_gas_domestic"
  ],
  Ene5: [
    // AutoGeneración Gas Licuado
    "glp_cogeneration",
    "glp_combined",
    "glp_production_operations",
    "glp_domestic"
  ],
  Ene6: [
    // Fuel Oil
    "heavy_fuel_oil_total",
    "light_fuel_oil_total",
  ],
  Ene7: [
    // AutoGeneración Carbón
    "coal_total",
  ],
  Ene8: [
    // AutoGeneración Diesel
    "diesel_cogeneration",
    "diesel_combined",
    "diesel_production_operations",
    "diesel_forklifts"
  ],
  Ene9: [
    // AutoGeneración Biodiesel
    "biodiesel_cogeneration",
    "biodiesel_combined",
    "biodiesel_production_operations",
    "biodiesel_forklifts"

  ],
  Ene10: [
    // gasolina
    "gasoline_combined",
    "gasoline_cogeneration",
    "gasoline_production_operations",
    "gasoline_forklifts"
  ],
  Ene11: [
    // Gasolina + Ethanol (sumados)
    "biofuel_combined",
    "biofuel_cogeneration",
    "biofuel_production_operations",
    "biofuel_forklifts"
  ],
  Ene12: [
    // Biomasa
    "biomass_total",
  ],
  Ene13: [
    // Vapor
    "steam_total",
  ],
};

export const energyGroupLabels = {
  Ene1: "Red",
  Ene2: "PPA",
  Ene3: "Solar",
  Ene4: "Gas Natural",
  Ene5: "GLP",
  Ene6: "Fuel Oil",
  Ene7: "Carbón",
  Ene8: "Diesel",
  Ene9: "Biodiesel",
  Ene10:"Gasolina",
  Ene11: "Biocombustible",
  Ene12: "Biomasa",
  Ene13: "Vapor",
};

// `type` (arriba) queda fijo en español porque es la clave interna que
// matchea contra DONUT_COLORS.energy (colores) — el texto que se muestra
// (pills, tooltip, leyenda del PDF) se traduce por separado con este mapa,
// sin tocar `energyGroupLabels`. Mismo patrón que en Social/General.
export const ENERGY_GROUP_LABEL_KEYS = {
  "Red": "energy_source_grid",
  "PPA": "energy_source_ppa",
  "Solar": "energy_source_solar",
  "Gas Natural": "natural_gas",
  "GLP": "energy_source_lpg",
  "Fuel Oil": "energy_source_fuel_oil",
  "Carbón": "coal",
  "Diesel": "diesel",
  "Biodiesel": "biodiesel",
  "Gasolina": "energy_source_gasoline",
  "Biocombustible": "biofuel",
  "Biomasa": "biomass",
  "Vapor": "steam",
};

/**
 * Suma los valores de cada grupo (Ene1, Ene2, ...) para un año dado
 * Convierte todos los valores a MWh para poder compararlos correctamente
 * @param {Object} totalsPerYear - datos como { biodiesel_cogeneration: {2025: { total, unit }}}
 * @param {Number|String} year - año a calcular
 * @returns {Object} resultados agrupados por Ene# en MWh
 */
export const getAnnualTotalsByGroup = (totalsPerYear, year) => {
  const results = {};
  const TARGET_UNIT = "MWh"; // Unidad común para comparación

  Object.entries(energyGroups).forEach(([groupKey, conceptKeys]) => {
    let totalMWh = 0;

    conceptKeys.forEach((concept) => {
      const value = totalsPerYear?.[concept]?.[year]?.total || 0;
      const sourceUnit = totalsPerYear?.[concept]?.[year]?.unit || "";

      if (value > 0 && sourceUnit) {
        // Convertir a MWh equivalentes usando los factores de conversión por fuente
        const convertedValue = convertEnergy2(value, sourceUnit, "mwh", concept, true);
        if (convertedValue !== null && !isNaN(convertedValue)) {
          totalMWh += convertedValue;
        } else {
          // Si no se puede convertir, intentar conversión directa kWh->MWh
          if (sourceUnit.toLowerCase() === "kwh") {
            totalMWh += value * 0.001;
          } else if (sourceUnit.toLowerCase() === "mwh") {
            totalMWh += value;
          } else {
            console.warn(`No se pudo convertir ${concept}: ${value} ${sourceUnit} a MWh`);
          }
        }
      }
    });

    results[groupKey] = { total: totalMWh, unit: TARGET_UNIT };
  });

  return results;
};

/////////////Barras consumo
export const buildBarChartData = (data) => {
  if (!data?.total_electricity_kpis) {
    console.warn("⚠️ No existe data.total_electricity_kpis");
    return [];
  }

  // Filtrar solo años con datos completos (total > 0) ENERGIA ELECTRICA TOTAL
  const result = Object.entries(data.total_electricity_kpis)
    .filter(([year, { total }]) => total > 0)
    .map(([year, { total, unit }]) => ({
      year,
      value: total,
      unit,
    }))
    // Ordenar por año descendente, tomar los últimos 5 y volver a ordenar ascendente
    .sort((a, b) => parseInt(b.year) - parseInt(a.year))
    .slice(0, 5)
    .sort((a, b) => parseInt(a.year) - parseInt(b.year));

  return result;
};

const costKeys = [
  "electricity_cost_total",
  "renewable_energies_ppa_cost",
  "renewable_energies_on_site_maintenance_and_amortization",
  // "natural_gas_total_cost",
  // "glp_total_cost",
  // "diesel_total_cost",
  // "biodiesel_total_cost",
  // "gasoline_total_cost",
  // "biomass_total_cost",
  // "steam_total_cost",
];

// Función para construir los datos del gráfico
export const buildBarChartDataCost = (data) => {
  if (!data) return [];

  // Obtenemos todos los años disponibles desde uno de los indicadores
  const years = Object.keys(data[costKeys[0]] || {});
  if (years.length === 0) return [];

  // Filtrar solo años con datos completos (total > 0)
  return years
    .map((year) => {
      const costData = costKeys.map((key) => ({
        total: data[key]?.[year]?.total || 0,
        unit: data[key]?.[year]?.unit,
      }));

      // Buscar primera unidad válida
      const unit = costData.find((item) => item.unit)?.unit;

      // Buscar abreviatura según esa unidad
      const short =
        coins_units.find((c) => c.value === unit)?.short || unit || "";

      // Sumar todos los costos del año
      const total = costData.reduce(
        (sum, item) => sum + (item.total || 0),
        0,
      );

      return {
        year,
        value: !isNaN(total) ? parseFloat(total.toFixed(2)) : 0,
        unit, // nombre completo
        short, // abreviatura para mostrar
      };
    })
    .filter((item) => item.value > 0) // Solo años con costos
    // Ordenar por año descendente, tomar los últimos 5 y volver a ordenar ascendente
    .sort((a, b) => parseInt(b.year) - parseInt(a.year))
    .slice(0, 5)
    .sort((a, b) => parseInt(a.year) - parseInt(b.year));
};

const TableGraf = ({ filters, totales, t }) => {
  const { year } = filters || {};
  const [dataConsumo, setDataConsumo] = useState([]);
  const [data, setData] = useState([]);
  const windowWidth = useWindowWidth();

  // Memoizar configuración responsive para evitar recalcular en cada render
  const responsiveConfig = useMemo(() => {
    const isMobile = windowWidth <= 768;
    const isSmallMobile = windowWidth <= 480;
    const isTablet = windowWidth > 768 && windowWidth <= 992;
    const isNotebook = windowWidth >= 1024 && windowWidth <= 1500;

    const isBelow1100 = windowWidth <= 1100;

    return {
      isMobile,
      isSmallMobile,
      isTablet,
      isNotebook,
      isBelow1100,
      // Tamaños de fuente consistentes
      fontSize: {
        large: isSmallMobile ? 12 : isMobile ? 14 : 16,
        medium: isSmallMobile ? 10 : isMobile ? 11 : 12,
        small: isSmallMobile ? 9 : isMobile ? 10 : 11,
      },
      // Alturas de gráficos
      chartHeight: {
        donut: getDonutHeight(isNotebook, isMobile),
        bar: isSmallMobile ? 250 : isMobile ? 280 : 300,
      },
    };
  }, [windowWidth]);

  useEffect(() => {
    if (!totales || !year) return;

    // console.log("📊 TableGraf - totales recibidos:", totales);
    // console.log("📊 TableGraf - total_energy_kpis en totales:", totales?.total_energy_kpis);

    const consumoData = getAnnualTotalsByGroup(totales, filters.year);

    const data = totales;

    setDataConsumo(consumoData);
    setData(data);
  }, [filters, totales, year]);

  const energyColorMapDonut = DONUT_COLORS.energy;

  const barPalette = BAR_PALETTE;

  const EnergyDonut = ({ totalsPerYear }) => {
    const [hiddenTypes, setHiddenTypes] = useState([]);

    const allData = useMemo(() =>
      Object.entries(totalsPerYear)
        .filter(([_, { total }]) => total > 0)
        .map(([groupKey, { total, unit }]) => ({
          type: energyGroupLabels[groupKey],
          value: total,
          unit,
        })), [totalsPerYear]);

    const chartData = useMemo(() => {
      const visible = allData.filter(d => !hiddenTypes.includes(d.type));
      const totalVisible = visible.reduce((sum, d) => sum + d.value, 0);
      return visible.map(d => ({
        ...d,
        percent: totalVisible ? (d.value / totalVisible) * 100 : 0,
      }));
    }, [allData, hiddenTypes]);

    const totalVisible = chartData.reduce((sum, d) => sum + d.value, 0);

    const config = {
      data: chartData,
      angleField: "value",
      colorField: "type",
      scale: {
        color: {
          domain: Object.keys(energyColorMapDonut),
          range: Object.values(energyColorMapDonut),
        },
      },
      radius: 1,
      innerRadius: 0.6,
      height: responsiveConfig.chartHeight.donut,
      label: false,
      legend: false,
      statistic: {
        title: false,
        content: {
          content: `${t("total")}\n${formatWhole(totalVisible)}`,
          style: {
            fontSize: responsiveConfig.fontSize.large,
            fontWeight: 300,
            whiteSpace: "pre-line",
          },
        },
      },
      tooltip: {
        title: (datum) => t(ENERGY_GROUP_LABEL_KEYS[datum.type] || datum.type),
        items: [
          (datum) => ({
            name: "",
            value: `${formatWhole(datum.value)} ${datum.unit || ""} (${formatPercentage(datum.percent)})`,
          }),
        ],
      },
      interactions: [{ type: "element-active" }],
    };

    if (!allData.length) {
      return (
        <EmptyChartMessage
          message={
            t("complete_at_least_one_period") ||
            "Complete al menos un período para visualizar este indicador"
          }
        />
      );
    }

    return (
      <>
        <div className="chartWrapper">
          <Pie {...config} />
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: responsiveConfig.isBelow1100 ? "repeat(2, 1fr)" : "repeat(4, 1fr)",
            alignItems: "start",
            gap: "8px",
            marginTop: "15px",
            borderTop: "1px solid #000",
            paddingTop: "15px",
            // Mismo min-height que .pillsRow (tableGraf.shared.scss, 130px):
            // .chartCard es flex-column con .chartWrapper en flex:1, así que
            // si este bloque reserva menos alto que .pillsRow, el gráfico de
            // dona crece de más para compensar y la línea negra (borderTop)
            // termina en una altura distinta a la de las cards de barras.
            minHeight: "130px",
          }}
        >
          {allData.map((item) => {
            const isHidden = hiddenTypes.includes(item.type);
            const color = energyColorMapDonut[item.type] || "#D9D9D9";
            return (
              <div
                key={item.type}
                onClick={() =>
                  setHiddenTypes(prev =>
                    prev.includes(item.type)
                      ? prev.filter(y => y !== item.type)
                      : [...prev, item.type]
                  )
                }
                className="yearPill"
                style={{
                  backgroundColor: isHidden ? "#E5E7EB" : color,
                  color: isHidden ? "#9CA3AF" : "#FFF",
                  padding: "6px 4px",
                  borderRadius: "20px",
                  fontSize: "10px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  border: `1px solid ${isHidden ? "#D1D5DB" : color}`,
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "28px",
                  opacity: isHidden ? 0.7 : 1,
                  textDecoration: isHidden ? "line-through" : "none",
                  userSelect: "none",
                }}
              >
                {t(ENERGY_GROUP_LABEL_KEYS[item.type] || item.type)}
              </div>
            );
          })}
        </div>
      </>
    );
  };

  const EnergyBarChart = ({ data }) => {
    const rawData = buildBarChartData(data);
    const allYears = rawData.map(d => d.year);
    const [activeYears, setActiveYears] = useState(allYears);

    const colorMap = useMemo(() => {
      const map = {};
      allYears.forEach((y, i) => { map[y] = barPalette[i] || "#ccc"; });
      return map;
    }, [allYears]);

    const maxValue = Math.max(...rawData.map((d) => d.value));
    const SHOULD_SCALE = maxValue < 0.01;
    const SCALE_FACTOR = SHOULD_SCALE ? 1000 : 1;

    const chartData = rawData
      .filter(d => activeYears.includes(d.year))
      .map((d) => ({
        year: d.year,
        unit: d.unit,
        displayValue: d.value * SCALE_FACTOR,
        realValue: d.value,
      }));

    const config = {
      data: chartData,
      xField: "year",
      yField: "displayValue",
      autoFit: true,
      colorField: "year",
      scale: { color: { domain: Object.keys(colorMap), range: Object.values(colorMap) } },
      height: responsiveConfig.chartHeight.bar,
      legend: false,
      tooltip: {
        shared: true,
        title: (d) => `Año ${d.year}`,
        items: [
          (datum) => ({
            name: "Energía Eléctrica Total",
            value: `${formatMagnitudeAdaptive(datum.realValue)} ${datum.unit}`,
          }),
        ],
      },
      // @ant-design/plots v2 (G2) no reconoce xAxis/yAxis — esas claves son
      // de la API vieja (v1) y quedaban ignoradas silenciosamente: título,
      // formatter y estilos de acá nunca se aplicaban en pantalla. La forma
      // correcta es axis.x/axis.y, con las mismas propiedades que ya se usan
      // en EnergyKPIsDashBoard.js (labelFill, labelFormatter, etc.).
      axis: {
        // Sin títulos de eje en pantalla (se ven en el tooltip); el PDF sí
        // los muestra — ver renderEnergyChartsForPdf en pdfEnergyCharts.js.
        x: {
          title: false,
          labelFontSize: responsiveConfig.fontSize.medium,
          labelFill: "#000",
        },
        y: {
          title: false,
          labelFontSize: responsiveConfig.fontSize.small,
          labelFill: "#000",
          labelFormatter: (v) => {
            if (responsiveConfig.isMobile && v >= 1000) {
              return `${(v / 1000).toFixed(1)}K`;
            }
            return formatWhole(v);
          },
        },
      },
      interactions: [{ type: "active-region" }],
    };

    return (
      <>
        <div className="chartWrapper"><Column {...config} /></div>
        {SHOULD_SCALE && (
          <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
            Nota: los valores del gráfico están escalados (×1.000) para
            facilitar la lectura. Los valores reales se muestran en el tooltip.
          </div>
        )}
        <div
          className="pillsRow"
          style={{
            display: "grid",
            gridTemplateColumns: responsiveConfig.isBelow1100 ? "repeat(2, 1fr)" : "repeat(5, 1fr)",
          }}
        >
          {allYears.map(year => (
            <div
              key={year}
              className="yearPill"
              onClick={() => setActiveYears(prev =>
                prev.includes(year) ? prev.filter(y => y !== year) : [...prev, year]
              )}
              style={{
                backgroundColor: activeYears.includes(year) ? colorMap[year] : "#E5E7EB",
                color: activeYears.includes(year) ? "white" : "#9CA3AF",
                border: `1px solid ${activeYears.includes(year) ? colorMap[year] : "#D1D5DB"}`,
                opacity: activeYears.includes(year) ? 1 : 0.7,
                textDecoration: activeYears.includes(year) ? "none" : "line-through",
                cursor: "pointer",
                transition: "all 0.2s ease",
                userSelect: "none",
              }}
            >
              {year}
            </div>
          ))}
        </div>
      </>
    );
  };

  const EnergyBarChartCost = ({ data }) => {
    const rawData = buildBarChartDataCost(data);
    const allYears = rawData.map(d => d.year);
    const [activeYears, setActiveYears] = useState(allYears);

    const colorMap = useMemo(() => {
      const map = {};
      allYears.forEach((y, i) => { map[y] = barPalette[i] || "#ccc"; });
      return map;
    }, [allYears]);

    const maxValue = Math.max(...rawData.map((d) => d.value));

    const chartData = rawData
      .filter(d => activeYears.includes(d.year))
      .map((d) => {
        const isSmall = d.value < 1;
        return {
          year: d.year,
          unit: d.unit,
          short: d.short,
          displayValue: isSmall ? maxValue * 0.1 : d.value,
          realValue: d.value,
          isSmall,
        };
      });

    if (!rawData.length) {
      return (
        <EmptyChartMessage
          message={
            t("complete_at_least_one_period") ||
            "Complete al menos un período para visualizar este indicador"
          }
        />
      );
    }

    const config = {
      data: chartData,
      xField: "year",
      yField: "displayValue",
      autoFit: true,
      colorField: "year",
      scale: { color: { domain: Object.keys(colorMap), range: Object.values(colorMap) } },
      height: responsiveConfig.chartHeight.bar,
      legend: false,
      tooltip: {
        shared: true,
        title: (d) => `Año ${d.year}`,
        items: [
          (datum) => {
            const value = datum?.realValue ?? datum?.value ?? 0;
            const short = datum?.short || "";
            return {
              name: "Costo Total",
              value: `${formatWhole(value)} ${short}`,
            };
          },
        ],
      },
      // Ver comentario en EnergyBarChart: xAxis/yAxis es la API vieja (v1),
      // @ant-design/plots v2 la ignora — la forma correcta es axis.x/axis.y.
      axis: {
        // Sin títulos de eje en pantalla (se ven en el tooltip); el PDF sí
        // los muestra — ver renderEnergyChartsForPdf en pdfEnergyCharts.js.
        x: {
          title: false,
          labelFontSize: responsiveConfig.fontSize.medium,
          labelFill: "#000",
          labelAutoRotate: responsiveConfig.isMobile ? false : true,
          labelAutoHide: false,
        },
        y: {
          title: false,
          labelFontSize: responsiveConfig.fontSize.small,
          labelFill: "#000",
          labelFormatter: (v) => {
            if (v < 2) return "~0";
            if (responsiveConfig.isMobile && v >= 1000000) {
              return `${Math.round(v / 1000000).toLocaleString("es-AR")}M`;
            }
            if (responsiveConfig.isMobile && v >= 1000) {
              return `${Math.round(v / 1000).toLocaleString("es-AR")}K`;
            }
            return Math.round(v).toLocaleString("es-AR");
          },
        },
      },
      interactions: [{ type: "active-region" }],
    };

    return (
      <>
        <div className="chartWrapper"><Column {...config} /></div>
        <div
          className="pillsRow"
          style={{
            display: "grid",
            gridTemplateColumns: responsiveConfig.isBelow1100 ? "repeat(2, 1fr)" : "repeat(5, 1fr)",
          }}
        >
          {allYears.map(year => (
            <div
              key={year}
              className="yearPill"
              onClick={() => setActiveYears(prev =>
                prev.includes(year) ? prev.filter(y => y !== year) : [...prev, year]
              )}
              style={{
                backgroundColor: activeYears.includes(year) ? colorMap[year] : "#E5E7EB",
                color: activeYears.includes(year) ? "white" : "#9CA3AF",
                border: `1px solid ${activeYears.includes(year) ? colorMap[year] : "#D1D5DB"}`,
                opacity: activeYears.includes(year) ? 1 : 0.7,
                textDecoration: activeYears.includes(year) ? "none" : "line-through",
                cursor: "pointer",
                transition: "all 0.2s ease",
                userSelect: "none",
              }}
            >
              {year}
            </div>
          ))}
        </div>
      </>
    );
  };

  // ======================
  // 🎨 RENDER
  // ======================

  // ======================
  // 🎨 RENDER
  // ======================


  return (
    <div className="energyDashboardGrid">
      <div className="chartCard">
        <h3 className="cardTitle">    {t("energy_sources")}</h3>
        <EnergyDonut totalsPerYear={dataConsumo} year={filters.year} />
      </div>

      <div className="chartCard">
        <h3 className="cardTitle">  {t("electric_energy_consumption")}</h3>
        <EnergyBarChart data={data} />
      </div>

      <div className="chartCard">
        <h3 className="cardTitle">        {t("electric_energy_costs")}</h3>
       <EnergyBarChartCost data={data} />
      </div>
    </div>
  );
};

export default TableGraf;
